import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { COLORS, FONTS } from '../../constants/config';
import { organizationsApi } from '../../services/apiService';
import { Organization } from '../../types';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { invalidate as invalidateData } from '../../utils/dataInvalidation';
import Svg, { Path } from 'react-native-svg';

// Back Icon
const BackIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 12H5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 19L5 12L12 5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

type RouteParams = {
  EditOrganization: {
    organizationId: number;
  };
};

interface FormData {
  name: string;
  vision: string;
  mission: string;
  description: string;
  values: string;
  founding_year: string;
  primary_color: string;
  secondary_color: string;
}

export default function EditOrganizationScreen() {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<RouteParams, 'EditOrganization'>>();
  const { organizationId } = route.params;
  const { showSuccess, showError } = useNotification();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    headerTitle: colors.textPrimary,
    label: colors.textSecondary,
    inputBg: isDark ? colors.backgroundAlt : '#F9FAFB',
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    loadingText: colors.textMuted,
    cancelBtnBorder: colors.border,
    cancelBtnText: colors.textMuted,
    actionBorder: colors.border,
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    vision: '',
    mission: '',
    description: '',
    values: '',
    founding_year: '',
    primary_color: '#2563EB',
    secondary_color: '#A6813F',
  });

  const fetchOrganization = useCallback(async () => {
    try {
      setLoading(true);
      const data = await organizationsApi.get(organizationId);
      setOrganization(data);
      setFormData({
        name: data.name || '',
        vision: data.vision || '',
        mission: data.mission || '',
        description: data.description || '',
        values: data.values || '',
        founding_year: data.founding_year?.toString() || '',
        primary_color: data.primary_color || '#2563EB',
        secondary_color: data.secondary_color || '#A6813F',
      });
    } catch (error) {
      console.error('Error fetching organization:', error);
      showError(t('messages.error.loadingOrganization'));
    } finally {
      setLoading(false);
    }
  }, [organizationId, showError]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  const handleSave = async () => {
    // Org name is read-only (managed in SSO) — no validation / not submitted.
    if (!formData.vision.trim()) {
      showError(t('validation.visionRequired'));
      return;
    }
    if (!formData.mission.trim()) {
      showError(t('validation.missionRequired'));
      return;
    }
    if (!formData.values.trim()) {
      showError(t('validation.valuesRequired'));
      return;
    }

    try {
      setSaving(true);
      await organizationsApi.update(organizationId, {
        vision: formData.vision.trim(),
        mission: formData.mission.trim(),
        values: formData.values.trim(),
        founding_year: formData.founding_year ? parseInt(formData.founding_year) : null,
      });
      showSuccess(t('messages.success.organizationUpdated'));
      invalidateData('my-organization');
      navigation.goBack();
    } catch (error: any) {
      console.error('Error updating organization:', error);
      showError(error.response?.data?.detail || t('messages.error.updatingOrganization'));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('organizations.editOrganization') },
          ]}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loading')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('navigation.home') },
          { label: t('organizations.editOrganization') },
        ]}
      />
      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
              <BackIcon size={20} color={colors.iconDefault} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: themeColors.headerTitle }]}>{t('organizations.editOrganizationData')}</Text>
          </View>

          {/* Form Card */}
          <View style={[styles.formCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder, borderWidth: 1 }]}>
            {/* Organization Name — sourced from SSO, NOT editable here.
                The name lives in the SSO directory; it is shown read-only so
                the Section Head can see it but cannot change it. It is also
                excluded from the save payload below. */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.name')}</Text>
              <TextInput
                style={[styles.input, styles.inputDisabled]}
                value={formData.name}
                editable={false}
                selectTextOnFocus={false}
              />
              <Text style={styles.helperText}>{t('organizations.nameFromSso', 'اسم الجهة يُدار من النظام الموحد (SSO) ولا يمكن تعديله')}</Text>
            </View>

            {/* Vision */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.vision')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={[styles.input, styles.multilineInput]}
                value={formData.vision}
                onChangeText={(text) => updateField('vision', text)}
                placeholder={t('organizations.enterVision')}
                placeholderTextColor="#9CA3AF"
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            {/* Mission */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.mission')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={[styles.input, styles.multilineInput]}
                value={formData.mission}
                onChangeText={(text) => updateField('mission', text)}
                placeholder={t('organizations.enterMission')}
                placeholderTextColor="#9CA3AF"
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            {/* Description (نبذة عن الجهة) — sourced from SSO, NOT editable
                here. Like the name, it is owned by the SSO directory and shown
                read-only; it is excluded from the save payload below. */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.about')}</Text>
              <TextInput
                style={[styles.input, styles.multilineInput, styles.inputDisabled, { minHeight: 120 }]}
                value={formData.description}
                editable={false}
                selectTextOnFocus={false}
                multiline
                numberOfLines={5}
                textAlignVertical="top"
              />
              <Text style={styles.helperText}>{t('organizations.descriptionFromSso', 'نبذة عن الجهة تُدار من النظام الموحد (SSO) ولا يمكن تعديلها')}</Text>
            </View>

            {/* Values */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.values')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={[styles.input, styles.multilineInput]}
                value={formData.values}
                onChangeText={(text) => updateField('values', text)}
                placeholder={t('organizations.enterValues')}
                placeholderTextColor="#9CA3AF"
                multiline
                numberOfLines={2}
                textAlignVertical="top"
              />
            </View>

            {/* Founding Year — عدد الموظفين is sourced automatically from SSO,
                so it's no longer an editable field here. */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.foundingYear')}</Text>
              <TextInput
                style={styles.input}
                value={formData.founding_year}
                onChangeText={(text) => updateField('founding_year', text.replace(/[^0-9]/g, ''))}
                placeholder={t('organizations.foundingYearExample')}
                placeholderTextColor="#9CA3AF"
                keyboardType="numeric"
                maxLength={4}
              />
            </View>

            {/* Action Buttons */}
            <View style={styles.actionRow}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => navigation.goBack()}
                disabled={saving}
              >
                <Text style={styles.cancelBtnText}>{t('common.cancel')}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={COLORS.white} />
                ) : (
                  <Text style={styles.saveBtnText}>{t('common.saveChanges')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.textMuted,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 24,
  },
  backBtn: {
    padding: 8,
    marginLeft: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    color: '#111827',
  },
  formCard: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 24,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#374151',
    marginBottom: 8,
    textAlign: 'right',
  },
  required: {
    color: '#EF4444',
  },
  input: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#111827',
    textAlign: 'right',
  },
  inputDisabled: {
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
  },
  helperText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    marginTop: 6,
    textAlign: 'right',
  },
  multilineInput: {
    minHeight: 80,
    paddingTop: 12,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 16,
  },
  halfWidth: {
    flex: 1,
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 12,
    marginTop: 24,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  cancelBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: COLORS.white,
  },
  cancelBtnText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#6B7280',
  },
  saveBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: COLORS.primary,
    minWidth: 140,
    alignItems: 'center',
  },
  saveBtnDisabled: {
    opacity: 0.6,
  },
  saveBtnText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.white,
  },
});
